import React, { useState, useEffect } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from 'easy-peasy';

import { Wrapper, Page, Sidebar, Form, Field, Button, Row, Fieldset, Select, Checkbox, PrimaryText, RegisterSidebarInformations } from '../../components';
import {Background, Container, AddAnotherButton, Content, SmallText} from './Register.styled';

import { FieldExtender } from '../../components/Field/Field';

import * as registrationService from '../../services/registration.service';
import { getCorrectTermsFileUrl } from '../../data/terms';

import countries from '../../data/countries';

export default () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [serialNumberInputsCount, setSerialNumberInputsCount] = useState(1);
  const addLoader = useStoreActions(actions => actions.addActiveLoader);
  const removeLoader = useStoreActions(actions => actions.removeActiveLoader);

  const user = useStoreState(store => store.user);

  const increaseSerialNumberInputs = (append) => {
    const count = serialNumberInputsCount + 1;
    setSerialNumberInputsCount(count);
    append()
  }

  const handleSubmit = setError => data => {
    addLoader();
    registrationService.register(data)
      .then(() => {
        removeLoader();
        history.push('/register/ty');
      })
      .catch(error => {
        removeLoader();
        if (error && error.invalid_fields) {
          error.invalid_fields.forEach(invalid => {
            if (invalid.field === 'serial_numbers') {
              setError(`${invalid.field}${invalid.index}`, invalid.rule, t(invalid.rule))
            } else {
              setError(invalid.field, invalid.rule, t(invalid.rule))
            }
          })
        }
      })
  }

  return (
    <Background>
      <Wrapper>
        <Container>
          <Page title={t('registerProductsHeading')}>
            <Sidebar>
              <RegisterSidebarInformations />
            </Sidebar>
            <Content>
              <Form
                onSubmit={handleSubmit}
                config={{
                  defaultValues: {
                    first_name: '',
                    surname: '',
                    phone_number: '',
                    address_street_number: '',
                    email: '',
                    address_postcode: '',
                    address_city: '',
                    address_country: '',
                    answer_1: '',
                    answer_2: '',
                    serial_numbers: ['']
                  }
                }}
              >
                {({ register, control, errors, triggerValidation, formState, clearError, getValues }) => {
                  const { fields, append, remove } = useFieldArray(
                    {
                      control,
                      name: "serial_numbers",
                    }
                  );

                  return (
                    <>
                      <Fieldset>
                        <Row>
                          <Field
                            label={t('firstName')}
                            id='first-name'
                            error={errors.first_name}
                            required
                          >
                            <input
                              id='first-name'
                              name='first_name'
                              ref={register}
                              placeholder={t('firstName')}
                            />
                          </Field>
                          <Field
                            label={t('surname')}
                            id='surname'
                            error={errors.surname}
                            required
                          >
                            <input
                              id='surname'
                              name='surname'
                              ref={register}
                              placeholder={t('surname')}
                            />
                          </Field>
                        </Row>
                        <Row>
                          <Field
                            label={t('emailAddress')}
                            id='email'
                            error={errors.email}
                            required
                          >
                            <input
                              id='email'
                              name='email'
                              ref={register}
                              placeholder={t('emailAddress')}
                            />
                          </Field>
                          <Field
                            label={t('phoneNumber')}
                            id='phone-number'
                            error={errors.phone_number}
                            required
                          >
                            <input
                              id='phone-number'
                              name='phone_number'
                              ref={register}
                              placeholder={t('phoneNumber')}
                            />
                          </Field>
                        </Row>
                      </Fieldset>
                      <Fieldset>
                        <Row>
                          <Field
                            label={t('address')}
                            id='address'
                            error={errors.address_street_number}
                            required
                          >
                            <input
                              id='address'
                              name='address_street_number'
                              ref={register}
                              placeholder={t('address')}
                            />
                          </Field>
                        </Row>
                        <Row>
                          <Field
                            label={t('city')}
                            id='city'
                            error={errors.address_city}
                            required
                          >
                            <input
                              id='city'
                              name='address_city'
                              ref={register}
                              placeholder={t('city')}
                            />
                          </Field>
                          <Field
                            label={t('postCode')}
                            id='postcode'
                            error={errors.address_postcode}
                            required
                          >
                            <input
                              id='postcode'
                              name='address_postcode'
                              ref={register}
                              placeholder={t('postCode')}
                            />
                          </Field>
                        </Row>
                        <Row>
                          <Field
                            label={t('country')}
                            id='country'
                            error={errors.address_country}
                            required
                          >
                            <Controller
                              as={
                                <Select
                                  options={countries}
                                />
                              }
                              control={control}
                              onChange={([selected, { action }]) => {
                                clearError(['address_country'])
                                return selected.value;
                              }}
                              error={errors.address_country}
                              name="address_country"
                            />
                          </Field>
                          <FieldExtender />
                        </Row>
                      </Fieldset>
                      <Fieldset>
                        { fields.map((field, index) => (
                          <Row key={field.id}>
                            <Field
                              label={`${t('serialNumber')} #${index}`}
                              id={`serial-${index}.serial`}
                              error={errors[`serial_numbers${index}`]}
                              remove={index > 0 ? () => remove(index) : undefined}
                            >
                              <input
                                id={`serial-${index}.serial`}
                                name={`serial_numbers[${index}]`}
                                ref={register()}
                                placeholder={t('enterSerialNumber')}
                                onChange={() => clearError(`serial_numbers${index}`) && triggerValidation('serial_numbers')}
                              />
                            </Field>
                            <FieldExtender />
                          </Row>
                        ))}
                        {/* {renderSerialNumberInputs(register)} */}
                        <Row>
                          <AddAnotherButton
                            type='button'
                            onClick={() => increaseSerialNumberInputs(append)}
                          >+ {t('addAnotherSerialNumber')}</AddAnotherButton>
                        </Row>
                      </Fieldset>
                      <Fieldset>
                        <Row>
                          <Field
                            label={t('whatDescribeYourSituation')}
                            id='situation'
                            error={errors.answer1}
                            required
                          >
                            <Controller
                              as={
                                <Select
                                  options={[
                                    { label: t('privateHomeOwner'), value: '1' },
                                    { label: t('commercialPremises'), value: '2' },
                                    { label: t('other'), value: '3' },
                                  ]}
                                />
                              }
                              control={control}
                              onChange={([selected, { action }]) => {
                                clearError(['answer1'])
                                return selected.value;
                              }}
                              error={errors.answer1}
                              name="answer1"
                            />
                          </Field>
                          <Field
                            label={t('whereDidUHearAboutUs')}
                            id='hear'
                            error={errors.answer2}
                            required
                          >
                            <Controller
                              as={
                                <Select
                                  options={[
                                    { label: t('web'), value: '1' },
                                    { label: t('refferal'), value: '2' },
                                    { label: t('wordOfMouth'), value: '3' },
                                    { label: t('installer'), value: '4' },
                                    { label: t('windowEnvelope'), value: '5' },
                                    { label: t('other'), value: '6' },

                                  ]}
                                />
                              }
                              control={control}
                              onChange={([selected, { action }]) => {
                                clearError(['answer2'])
                                return selected.value;
                              }}
                              error={errors.answer2}
                              name="answer2"
                            />
                          </Field>
                        </Row>
                      </Fieldset>
                      <Fieldset withoutSeparator>
                        <Row>
                          <SmallText>{t('zeroAcceptance', "")}</SmallText>
                        </Row>
                        <Row>
                          <Controller
                            as={(
                              <Checkbox error={errors.acceptance1}>
                                {(Text) => (
                                  <Text>{t('iHaveReadAndAccept')}<a target='_blank' href={`/${getCorrectTermsFileUrl(lang)}`}><PrimaryText><strong>{t('termsAndConditions')}</strong></PrimaryText></a></Text>
                                )}
                              </Checkbox>
                            )}
                            control={control}
                            onChange={([value]) => {
                              clearError(['acceptance1'])
                              return value;
                            }}
                            name='acceptance1'
                          />
                        </Row>
                        <Row>
                          <Controller
                            as={(
                              <Checkbox error={errors.acceptance2}>
                                {(Text) => (
                                  <Text>{t('secondAcceptance')}</Text>
                                )}
                              </Checkbox>
                            )}
                            control={control}
                            onChange={([value]) => {
                              clearError(['acceptance2'])
                              return value;
                            }}
                            name='acceptance2'
                          />
                        </Row>
                        <Row right>
                          <Button
                            primary
                            text={t('registerButton')}
                            type='submit'
                            loading={formState.isSubmitting}
                          />
                        </Row>
                      </Fieldset>
                    </>
                  )
                }}
              </Form>
            </Content>
          </Page>
        </Container>
      </Wrapper>
    </Background>
  );
}